<template>
  <div class="instance-details p-4 bg-gray-50 rounded-lg shadow-inner">
    <h2 class="text-xl font-bold mb-4">Instance details</h2>
    <InstanceDetailsHeader :instanceUrl="instanceUrl"
      :instanceStatus="instanceStatus":getInstanceSeverity="getInstanceSeverity" />
    
    <TabView class="instance-tabs">
      <TabPanel header="Storage information">
        <InstanceStorageInfo v-if="props.tenant" :tenant="props.tenant" />
        <div v-else class="text-center p-8 text-gray-500">
          <p>Tenant information is required to load storage details.</p>
        </div>
      </TabPanel>
      
      <TabPanel header="Cases created">
        <div class="cases-created p-4">
          <div class="filters mb-4 flex gap-4 bg-white p-4 rounded-lg shadow-sm">
            <div class="flex-1">
              <h3 class="text-lg font-semibold mb-3">Date range</h3>
              <div class="flex gap-4">
                <div class="flex-1">
                  <label class="block text-sm font-medium text-gray-600 mb-1">From</label>
                  <Calendar v-model="casesDateRange.from" showIcon dateFormat="yy-mm-dd" placeholder="YYYY-MM-DD" class="w-full" />
                </div>
                <div class="flex-1">
                  <label class="block text-sm font-medium text-gray-600 mb-1">To</label>
                  <Calendar v-model="casesDateRange.to" showIcon dateFormat="yy-mm-dd" placeholder="YYYY-MM-DD" class="w-full" />
                </div>
              </div>
            </div>
          </div>
          
          <InstanceCasesTable v-if="props.tenant"   :tenant="props.tenant"
            :start-date="formattedCasesStartDate" :end-date="formattedCasesEndDate"  />
           <div v-else class="text-center p-8 text-gray-500">
            <p>Tenant information is required to load cases.</p>
          </div>
        </div>
      </TabPanel>
      
      <TabPanel header="Users"><InstanceUsersTab :tenant="props.tenant" /></TabPanel>
      
      <TabPanel header="Instance parameters">
        <InstanceParamsTab v-if="props.tenant" :tenant="props.tenant" />
        <div v-else class="text-center p-8 text-gray-500">
          <p>Tenant information is required to load instance parameters.</p>
        </div>
      </TabPanel>
    </TabView>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import Calendar from 'primevue/calendar';
import InstanceCasesTable from './InstanceCasesTable.vue';
import { useDateFormatter } from '../composables/useDateFormatter';
import InstanceStorageInfo from './InstanceStorageInfo.vue';
import InstanceDetailsHeader from './InstanceDetailsHeader.vue';
import InstanceUsersTab from './InstanceUsersTab.vue';
import InstanceParamsTab from './InstanceParamsTab.vue';

const props = defineProps({
  customerId: {
    type: [String, Number],
    required: true
  },
  customerName: {
    type: String,
    default: 'Customer'
  },
  instanceUrl: {
    type: String,
    default: ''
  },
  instanceStatus: {
    type: String,
    default: 'Unknown'
  },
  tenant: {
    type: String,
    default: ''
  },
  instanceId: {
    type: [String, Number],
    default: null
  }
});

const casesDateRange = ref({
  from: null,
  to: null
});

const { formatDateForApi } = useDateFormatter();

const formattedCasesStartDate = computed(() => formatDateForApi(casesDateRange.value.from));
const formattedCasesEndDate = computed(() => formatDateForApi(casesDateRange.value.to));

onMounted(async () => {
  const today = new Date();
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(today.getDate() - 30);
  casesDateRange.value = { from: null, to: null };
  console.log(`Fetching instance data for customer ID: ${props.customerId}`);
});

const getInstanceSeverity = (status) => {
  switch(status) {
    case 'active': return 'success';
    case 'inactive': return 'danger';
    default: return 'secondary';
  }
};
</script>

<style scoped>
.instance-details {
  margin: 0.5rem 1rem 1.5rem;
}

:deep(.p-tabview-nav) {
  border-bottom: 1px solid #e2e8f0;
}

:deep(.p-tabview-nav li .p-tabview-nav-link) {
  background-color: #f8fafc;
  color: #64748b;
  padding: 1rem 1.5rem;
  font-weight: 600;
  border-top-left-radius: 0.5rem;
  border-top-right-radius: 0.5rem;
  transition: background-color 0.2s, color 0.2s;
}

:deep(.p-tabview-nav li .p-tabview-nav-link:not(.p-disabled):focus) {
  box-shadow: none;
}

:deep(.p-tabview-nav li.p-highlight .p-tabview-nav-link) {
  background-color: #fff;
  color: #3b82f6;
  border-color: #e2e8f0;
  border-bottom-color: #fff;
}

:deep(.p-tabview-panels) {
  padding: 0;
  background-color: #fff;
  border-bottom-left-radius: 0.5rem;
  border-bottom-right-radius: 0.5rem;
  border: 1px solid #e2e8f0;
  border-top: none;
}

/* Make calendar and multiselect inputs full width */
:deep(.p-calendar) {
  width: 100%;
}

:deep(.p-multiselect) {
  width: 100%;
}

/* Feature toggle styling */
.toggle-item {
  transition: all 0.2s ease-in-out;
}

.toggle-item:hover {
  background-color: #f8fafc;
}

:deep(.p-inputswitch.p-inputswitch-checked .p-inputswitch-slider) {
  background: #4ade80 !important;
}
</style> 